import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ─────────────────────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────────────────────

export const fetchUsers = createAsyncThunk('admin/fetchUsers',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await api.get('/api/v1/admin/users', { params }).then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const createUser = createAsyncThunk('admin/createUser',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.post('/api/v1/admin/users', payload).then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const updateUser = createAsyncThunk('admin/updateUser',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      // updateUser = change role + optionally status in one call
      if (payload.role) {
        await api.patch(`/api/v1/admin/users/${id}/role`, { role: payload.role })
      }
      if (payload.status) {
        await api.patch(`/api/v1/admin/users/${id}/status`, { status: payload.status })
      }
      // Re-fetch updated user by listing all (no single-user GET endpoint)
      const { data } = await api.get('/api/v1/admin/users').then(r => r.data)
      return data.find(u => String(u.id) === String(id)) || { id, ...payload }
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const toggleUserStatus = createAsyncThunk('admin/toggleUserStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      return await api.patch(`/api/v1/admin/users/${id}/status`, { status }).then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const assignUserRole = createAsyncThunk('admin/assignUserRole',
  async ({ id, role }, { rejectWithValue }) => {
    try {
      return await api.patch(`/api/v1/admin/users/${id}/role`, { role }).then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const assignUserTeam = createAsyncThunk('admin/assignUserTeam',
  // Team assignment not in current backend — update locally and skip API call
  async ({ id, team }, { getState, rejectWithValue }) => {
    try {
      // If a team endpoint exists later, call it here
      // For now return a local update shape
      return { id, team }
    } catch (e) {
      return rejectWithValue(e.message)
    }
  }
)

export const deleteUser = createAsyncThunk('admin/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/v1/admin/users/${id}`)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

// ─────────────────────────────────────────────────────────────────────────────
// Roles
// ─────────────────────────────────────────────────────────────────────────────

export const fetchRoles = createAsyncThunk('admin/fetchRoles',
  async (_, { rejectWithValue }) => {
    try {
      return await api.get('/api/v1/admin/roles').then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const createRole = createAsyncThunk('admin/createRole',
  async (payload, { rejectWithValue }) => {
    try {
      return await api.post('/api/v1/admin/roles', payload).then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const updateRole = createAsyncThunk('admin/updateRole',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      // PUT /roles/:id/permissions handles the full permission matrix
      return await api
        .put(`/api/v1/admin/roles/${id}/permissions`, { permissions: payload.permissions })
        .then(r => r.data)
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

export const deleteRole = createAsyncThunk('admin/deleteRole',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/v1/admin/roles/${id}`)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.detail || e.message)
    }
  }
)

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    users:       [],
    roles:       [],
    loadingUsers: false,
    loadingRoles: false,
    saving:       false,
    error:        null,
    userFilters: {
      search: '',
      status: '',
      role:   '',
      team:   '',
    },
  },
  reducers: {
    setUserFilter(state, { payload }) {
      state.userFilters = { ...state.userFilters, ...payload }
    },
    resetUserFilters(state) {
      state.userFilters = { search: '', status: '', role: '', team: '' }
    },
    clearError(state) {
      state.error = null
    },
  },
  extraReducers: (b) => {
    // ── fetchUsers ────────────────────────────────────────────────────────────
    b.addCase(fetchUsers.pending,   (s) =>    { s.loadingUsers = true;  s.error = null })
    b.addCase(fetchUsers.rejected,  (s, a) => { s.loadingUsers = false; s.error = a.payload })
    b.addCase(fetchUsers.fulfilled, (s, a) => {
      s.loadingUsers = false
      s.users = a.payload.data ?? a.payload ?? []
    })

    // ── createUser ────────────────────────────────────────────────────────────
    b.addCase(createUser.pending,   (s) =>    { s.saving = true;  s.error = null })
    b.addCase(createUser.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(createUser.fulfilled, (s, a) => {
      s.saving = false
      s.users  = [a.payload, ...s.users]
    })

    // ── updateUser ────────────────────────────────────────────────────────────
    b.addCase(updateUser.pending,   (s) =>    { s.saving = true;  s.error = null })
    b.addCase(updateUser.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(updateUser.fulfilled, (s, a) => {
      s.saving = false
      const idx = s.users.findIndex(u => String(u.id) === String(a.payload.id))
      if (idx !== -1) s.users[idx] = { ...s.users[idx], ...a.payload }
    })

    // ── toggleUserStatus ──────────────────────────────────────────────────────
    b.addCase(toggleUserStatus.pending,   (s) =>    { s.saving = true })
    b.addCase(toggleUserStatus.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(toggleUserStatus.fulfilled, (s, a) => {
      s.saving = false
      const idx = s.users.findIndex(u => String(u.id) === String(a.payload.id))
      if (idx !== -1) s.users[idx] = { ...s.users[idx], ...a.payload }
    })

    // ── assignUserRole ────────────────────────────────────────────────────────
    b.addCase(assignUserRole.pending,   (s) =>    { s.saving = true })
    b.addCase(assignUserRole.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(assignUserRole.fulfilled, (s, a) => {
      s.saving = false
      const idx = s.users.findIndex(u => String(u.id) === String(a.payload.id))
      if (idx !== -1) s.users[idx] = { ...s.users[idx], ...a.payload }
    })

    // ── assignUserTeam (local only) ───────────────────────────────────────────
    b.addCase(assignUserTeam.fulfilled, (s, a) => {
      const idx = s.users.findIndex(u => String(u.id) === String(a.payload.id))
      if (idx !== -1) s.users[idx] = { ...s.users[idx], team: a.payload.team }
    })

    // ── deleteUser ────────────────────────────────────────────────────────────
    b.addCase(deleteUser.pending,   (s) =>    { s.saving = true })
    b.addCase(deleteUser.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(deleteUser.fulfilled, (s, a) => {
      s.saving = false
      s.users  = s.users.filter(u => String(u.id) !== String(a.payload))
    })

    // ── fetchRoles ────────────────────────────────────────────────────────────
    b.addCase(fetchRoles.pending,   (s) =>    { s.loadingRoles = true;  s.error = null })
    b.addCase(fetchRoles.rejected,  (s, a) => { s.loadingRoles = false; s.error = a.payload })
    b.addCase(fetchRoles.fulfilled, (s, a) => {
      s.loadingRoles = false
      s.roles = a.payload.data ?? a.payload ?? []
    })

    // ── createRole ────────────────────────────────────────────────────────────
    b.addCase(createRole.pending,   (s) =>    { s.saving = true;  s.error = null })
    b.addCase(createRole.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(createRole.fulfilled, (s, a) => {
      s.saving = false
      s.roles  = [...s.roles, a.payload]
    })

    // ── updateRole ────────────────────────────────────────────────────────────
    b.addCase(updateRole.pending,   (s) =>    { s.saving = true;  s.error = null })
    b.addCase(updateRole.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(updateRole.fulfilled, (s, a) => {
      s.saving = false
      const idx = s.roles.findIndex(r => r.name === a.payload.name || r.id === a.payload.id)
      if (idx !== -1) s.roles[idx] = { ...s.roles[idx], ...a.payload }
    })

    // ── deleteRole ────────────────────────────────────────────────────────────
    b.addCase(deleteRole.pending,   (s) =>    { s.saving = true })
    b.addCase(deleteRole.rejected,  (s, a) => { s.saving = false; s.error = a.payload })
    b.addCase(deleteRole.fulfilled, (s, a) => {
      s.saving = false
      s.roles  = s.roles.filter(r => r.id !== a.payload && r.name !== a.payload)
    })
  },
})

export const { setUserFilter, resetUserFilters, clearError } = adminSlice.actions

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectUsers         = s => s.admin.users
export const selectRoles         = s => s.admin.roles
export const selectUserFilters   = s => s.admin.userFilters
export const selectUsersLoading  = s => s.admin.loadingUsers
export const selectRolesLoading  = s => s.admin.loadingRoles
export const selectSavingLoading = s => s.admin.saving
export const selectAdminError    = s => s.admin.error

export default adminSlice.reducer